import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

const RETURNS_FAQ_ITEMS = [
  {
    question: "תוך כמה זמן ניתן לבטל עסקה באליס תכשיטים?",
    answer: "ניתן לבטל את העסקה מיום הרכישה ועד תום שני ימים שלאחריו שאינם ימי מנוחה, בצירוף חשבונית ותעודת אחריות.",
  },
  {
    question: "מה ההבדל בין ביטול עסקה מתחת ל-3,000 ש\"ח לבין מעל?",
    answer: "עסקה עד 3,000 ש\"ח – ניתן לבטל ולקבל החזר כספי מלא. מעל 3,000 ש\"ח – יינתן זיכוי בלבד תמורת החזרת התכשיט, בהתאם לחוק הגנת הצרכן.",
  },
  {
    question: "כמה עולים דמי ביטול?",
    answer: "דמי ביטול עקב חרטה: 5% מערך העסקה או 100 ₪ – הנמוך מבניהם.",
  },
  {
    question: "תוך כמה זמן ניתן להחליף תכשיט?",
    answer: "החלפת תכשיט אפשרית עד 14 ימים מיום הקנייה (לא כולל ימי מנוחה), בצירוף חשבונית ותעודת אחריות.",
  },
  {
    question: "האם ניתן להחזיר תכשיט בהזמנה אישית?",
    answer: "לא. ביטול עסקה לא יחול על תכשיט בהזמנה אישית/מיוחדת שיוצר במיוחד עבור הלקוח, ולא יתקבל זיכוי כספי.",
  },
  {
    question: "מהי תקופת האחריות על תכשיטים?",
    answer: "אחריות על התכשיט היא שנה מיום הקנייה. האחריות אינה תקפה אם נעשה שימוש לא ראוי בתכשיט.",
  },
];

const Returns = () => {
  const { t, isHebrew } = useStaticTranslation("returns");
  const { t: seoT } = useStaticTranslation("seo");

  return (
    <PageTransition>
      <SEO
        title={seoT("returnsTitle", "מדיניות החזרות וביטולים")}
        description={seoT("returnsDescription", "מדיניות החזרות וביטולים של אליס תכשיטים - תנאי ביטול עסקה, החלפת תכשיטים, אחריות ותנאים מיוחדים")}
        canonical="https://alice.jewelry/returns"
        breadcrumbs={[
          { name: seoT("home", "דף הבית"), url: '/' },
          { name: seoT("breadcrumbReturns", "מדיניות החזרות"), url: '/returns' },
        ]}
        faqItems={RETURNS_FAQ_ITEMS}
      />
      <div className="min-h-screen flex flex-col" dir={isHebrew ? "rtl" : "ltr"}>
        <Header />
        
        <main className="flex-1">
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-12 animate-fade-in">
                <div className="gold-divider mb-6" />
                <h1 className="text-4xl md:text-5xl font-serif mb-4">{t("title", "מדיניות החזרות וביטולים")}</h1>
                <p className="text-lg text-muted-foreground">{t("subtitle", "ביטול עסקה בהתאם לחוק להגנת הצרכן התשמ\"א 1981- ומדיניות החזרת מוצרים")}</p>
              </div>

              <div className={`space-y-6 ${isHebrew ? 'text-right' : 'text-left'} animate-fade-in`} style={{ animationDelay: '0.1s' }}>
                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("returnTitle", "החזרת התכשיט וביטול העסקה")}</h2>
                  <p className="text-muted-foreground leading-relaxed">{t("returnDesc", "החזרת התכשיט וביטול העסקה בצירוף חשבונית ותעודת אחריות עד יומיים מיום הקניה.")}</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("under3000Title", "עסקאות עד 3,000 ש\"ח")}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t("under3000Desc", "עסקה לרכישת תכשיט, שמחירו לא עולה על 3,000 ש\"ח, ניתן לבטל מיום הרכישה ועד תום יומיים שלאחריו (שאינם ימי מנוחה).")}</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("over3000Title", "עסקאות מעל 3,000 ש\"ח")}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t("over3000Desc", "מעל סכום 3,000 ש\"ח יינתן זיכוי בלבד תמורת החזרת התכשיט בתוך יומיים מיום הקניה בהתאם לחוק הגנת הצרכן.")}</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("deadlineTitle", "מועד אחרון לביטול העסקה")}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t("deadlineDesc", "ניתן לבטל את העסקה מיום הרכישה ועד תום שני ימים שלאחריו שאינם ימי מנוחה.")}</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("cancellationFee", "דמי ביטול עקב חרטה")}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t("cancellationFeeDesc", "5% או 100 ₪ הנמוך מבניהם.")}</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("exchangeTitle", "החלפת התכשיט")}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t("exchangeDesc", "החלפת התכשיט בצירוף חשבונית ותעודת אחריות עד 14 ימים מיום הקנייה (לא כולל ימי מנוחה).")}</p>
                </div>

                <div className="bg-muted/30 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3">{t("specialConditions", "תנאים מיוחדים")}</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• {t("condition1", "ביטול עסקה לא יחולו על תכשיט בהזמנה אישית/מיוחדת שיוצרו במיוחד עבור הצרכן ולא יתקבל זיכוי כספי.")}</li>
                    <li>• {t("condition2", "תכשיטים שיצורו בעיצוב אישי או שינוי בתכשיט הקיים במלאי עבור הלקוח לא יינתן זיכוי/החזר כספי.")}</li>
                    <li>• {t("condition3", "דמי משלוח עבור החזרת תכשיט/שעון יחולו על הצרכן בלבד.")}</li>
                    <li>• {t("condition4", "לתכשיט תוצמד טיקט, הורדת הטיקט תבטל החזר כספי ותזכה בזיכוי להחלפת מוצר בלבד עד 14 יום ממועד הרכישה.")}</li>
                    <li>• {t("condition5", "אין החברה אחראית על אובדן / נזק ליהלום מרכזי בתכשיט יהלומים (מומלץ לעשות ביטוח).")}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("warrantyTitle", "אחריות")}</h3>
                  <p className="text-muted-foreground leading-relaxed">{t("warrantyDesc", "אחריות על התכשיט שנה מיום הקנייה. אין האחריות תקפה אם נעשה שימוש לא ראוי בתכשיט בכל דרך שהיא.")}</p>
                </div>

                {/* FAQ Section for AI Overviews */}
                <div className="pt-4">
                  <h2 className="text-2xl font-serif mb-6">שאלות נפוצות על החזרות וביטולים</h2>
                  <div className="space-y-6">
                    {RETURNS_FAQ_ITEMS.map((item, index) => (
                      <div key={index} className="border-b border-border pb-4">
                        <h3 className="text-lg font-medium mb-2 text-foreground">{item.question}</h3>
                        <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Returns;
